/**
 * SpareRadioCard — Available / Spare pool radios.
 *
 * Two indicators:
 *   1. Available — green dot when radio is in the rack.
 *                  Click it → inline text input appears to enter assignee name.
 *                  Type name + Enter (or click ✓) → radio goes blue with name.
 *                  Click blue name row → returns radio to rack (clears name).
 *
 *   2. Not Avail. — red dot. Click to mark radio missing/unavailable with no assignee.
 *                   Click again to clear back to available.
 */
import { useState, useRef, useEffect } from 'react'
import { Card, StatusDot, dotLabelColor } from '../ui'
import { WALKIE_ICON } from '../../assets'
import { markDirty } from '../../stores/sync'
import type { RadioSlot } from '../../stores/desktop'

interface SpareRadioCardProps {
  radio: RadioSlot
  onUpdate: (patch: Partial<RadioSlot>) => void
}

function lastName(name: string): string {
  const s = name.replace(/\s*\(.*?\)\s*/g, '').trim()
  return s.includes(',') ? s.split(',')[0].trim() : (s.split(' ').pop() ?? s)
}

export default function SpareRadioCard({ radio, onUpdate }: SpareRadioCardProps) {
  const [editing, setEditing] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const isOos      = !!radio.oos
  const isAssigned = !isOos && !radio.available && !!radio.assignedTo
  const isAvail    = !isOos && radio.available
  const isNotAvail = !isOos && !radio.available && !radio.assignedTo

  const borderColor = isOos      ? '#fca5a5'
                    : isAssigned ? '#3b82f6'
                    : isNotAvail ? '#f87171'
                    : editing    ? '#60a5fa'
                    : '#4ade80'

  const imgActive = isAssigned || isAvail || editing

  useEffect(() => {
    if (editing) inputRef.current?.focus()
  }, [editing])

  function handleAvailableClick() {
    if (isOos) return
    if (isAssigned) {
      // Return radio to rack
      markDirty()
      onUpdate({ available: true, assignedTo: '' })
    } else if (isAvail && !editing) {
      // Open inline input to assign
      setInputVal('')
      setEditing(true)
    } else if (isNotAvail) {
      markDirty()
      onUpdate({ available: true, assignedTo: '' })
    }
  }

  function commitAssign() {
    const name = inputVal.trim()
    setEditing(false)
    setInputVal('')
    if (name) {
      markDirty()
      onUpdate({ available: false, assignedTo: name })
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') commitAssign()
    if (e.key === 'Escape') { setEditing(false); setInputVal('') }
  }

  function handleNotAvail() {
    if (isOos) return
    setEditing(false)
    setInputVal('')
    markDirty()
    if (isNotAvail) {
      onUpdate({ available: true, assignedTo: '' })
    } else {
      onUpdate({ available: false, assignedTo: '' })
    }
  }

  return (
    <Card variant="item" borderColor={borderColor}>
      {/* Radio ID label */}
      <div style={{
        fontSize: 13, fontWeight: 700,
        color: 'var(--text-muted)', marginBottom: 4, textAlign: 'center',
      }}>
        Radio {radio.id}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        {/* Walkie icon */}
        <div style={{ flexShrink: 0, width: 60, height: 60 }}>
          <img
            src={WALKIE_ICON}
            alt={`Radio ${radio.id}`}
            style={{
              width: '100%', height: '100%', objectFit: 'contain',
              opacity: imgActive ? 1 : 0.4,
              filter:  imgActive ? 'none' : 'grayscale(100%)',
            }}
          />
        </div>

        <div style={{
          display: 'flex', flexDirection: 'column',
          alignItems: 'flex-start', gap: 4, flex: 1, minWidth: 0,
        }}>
          {isOos ? (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>OOS</span>
          ) : (
            <>
              {/* Row 1 — Available / Assigned */}
              {editing ? (
                // Inline input mode
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%' }}>
                  <input
                    ref={inputRef}
                    value={inputVal}
                    onChange={e => setInputVal(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={commitAssign}
                    placeholder="Name"
                    style={{
                      flex: 1, minWidth: 0,
                      background: 'var(--bg-deep)',
                      border: '1px solid #60a5fa',
                      borderRadius: 4,
                      color: 'var(--text-primary)',
                      fontSize: 11,
                      padding: '2px 6px',
                      outline: 'none',
                    }}
                  />
                  <span
                    onMouseDown={e => { e.preventDefault(); commitAssign() }}
                    style={{ fontSize: 13, cursor: 'pointer', color: '#60a5fa', fontWeight: 700 }}
                  >
                    ✓
                  </span>
                </div>
              ) : (
                <div
                  onClick={handleAvailableClick}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    cursor: 'pointer',
                    opacity: isAvail || isAssigned ? 1 : 0.3,
                  }}
                >
                  <StatusDot
                    variant={isAssigned ? 'blue' : 'green'}
                    active={isAvail || isAssigned}
                  />
                  {isAssigned ? (
                    <span style={{
                      fontSize: 11, fontWeight: 700, color: '#1d4ed8',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {lastName(radio.assignedTo)}
                    </span>
                  ) : (
                    <span style={{
                      fontSize: 11, fontWeight: 700,
                      color: dotLabelColor('green', isAvail),
                    }}>
                      Available
                    </span>
                  )}
                </div>
              )}

              {/* Row 2 — Not Available */}
              <div
                onClick={handleNotAvail}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  cursor: 'pointer', opacity: isNotAvail ? 1 : 0.3,
                }}
              >
                <StatusDot variant="red" active={isNotAvail} />
                <span style={{
                  fontSize: 11, fontWeight: 700,
                  color: dotLabelColor('red', isNotAvail),
                }}>
                  Not Avail.
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    </Card>
  )
}
